import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import StudentRegistrationForm from '../components/StudentRegistrationForm';
import { Modal, Toast } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return await res.json();
    } catch (err) {
      return {};
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  delete: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

function StudentsPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // Aayush action: open the Add Student form when navigated with ?aayush=add-student
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('aayush') === 'add-student') {
      setShowAddModal(true);
      params.delete('aayush');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    }
  }, []);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [filter, setFilter] = useState({ class_id: '', search: '' });
  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showColumnFilter, setShowColumnFilter] = useState(false);
  
  // All available columns
  const allColumns = [
    { key: 'admission_number', label: L('Adm. No.', 'प्रवेश क्र.'), default: true },
    { key: 'name', label: L('Name', 'नाम'), default: true },
    { key: 'name_hindi', label: L('Name (Hindi)', 'नाम (हिंदी)'), default: false },
    { key: 'class_name', label: L('Class', 'कक्षा'), default: true },
    { key: 'section_name', label: L('Section', 'सेक्शन'), default: false },
    { key: 'gender', label: L('Gender', 'लिंग'), default: true },
    { key: 'dob', label: L('Date of Birth', 'जन्म तिथि'), default: false },
    { key: 'primary_phone', label: L('Phone', 'फोन'), default: true },
    { key: 'father_name', label: L('Father', 'पिता'), default: true },
    { key: 'mother_name', label: L('Mother', 'माता'), default: false },
    { key: 'samagra_id', label: L('Samagra ID', 'समग्र ID'), default: true },
    { key: 'aadhaar_number', label: L('Aadhaar', 'आधार'), default: false },
    { key: 'category', label: L('Category', 'श्रेणी'), default: true },
    { key: 'religion', label: L('Religion', 'धर्म'), default: false },
    { key: 'caste', label: L('Caste', 'जाति'), default: false },
    { key: 'address_district', label: L('District', 'जिला'), default: false },
    { key: 'bank_account_number', label: L('Bank A/c', 'बैंक खाता'), default: false },
    { key: 'status', label: L('Status', 'स्थिति'), default: true },
  ];
  
  // Load saved column preferences or use defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('studentColumns');
    if (saved) {
      return JSON.parse(saved);
    }
    return allColumns.filter(c => c.default).map(c => c.key);
  });
  
  // Save column preferences
  const saveColumnPrefs = (cols) => {
    setVisibleColumns(cols);
    localStorage.setItem('studentColumns', JSON.stringify(cols));
    setShowColumnFilter(false);
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [studentsRes, classesRes] = await Promise.all([
      api.get('/api/students/', token),
      api.get('/api/classes/', token)
    ]);
    setStudents(studentsRes.students || []);
    setClasses(classesRes.classes || []);
    setLoading(false);
    setSelectedIds([]);
    setSelectAll(false);
  };

  const handleAddStudent = async (data) => {
    try {
      const res = await api.post('/api/students/', data, token);
      if (res.success) {
        setToast({ message: L('Student added successfully!', 'विद्यार्थी सफलतापूर्वक जोड़ा गया!'), type: 'success' });
        setShowAddModal(false);
        loadData();
      } else {
        setToast({ message: res.detail || L('Error adding student', 'विद्यार्थी जोड़ने में त्रुटि'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: L('Connection error', 'कनेक्शन त्रुटि'), type: 'error' });
    }
  };

  const handleUpdateStudent = async (data) => {
    try {
      const res = await api.put(`/api/students/${selectedStudent.id}`, data, token);
      if (res.success) {
        setToast({ message: L('Student updated successfully!', 'विद्यार्थी जानकारी अपडेट हो गई!'), type: 'success' });
        setShowViewModal(false);
        setSelectedStudent(null);
        setEditMode(false);
        loadData();
      } else {
        setToast({ message: res.detail || L('Error updating', 'अपडेट में त्रुटि'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: L('Connection error', 'कनेक्शन त्रुटि'), type: 'error' });
    }
  };

  const handleViewStudent = async (student) => {
    const res = await api.get(`/api/students/${student.id}`, token);
    setSelectedStudent(res.student || student);
    setShowViewModal(true);
    setEditMode(false);
  };

  // Select/Deselect student
  const handleSelectStudent = (studentId) => {
    setSelectedIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // Select/Deselect all filtered students
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(filteredStudents.map(s => s.id));
      setSelectAll(true);
    }
  };

  // Delete single student
  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm(L('Do you really want to delete this student?', 'क्या आप वाकई इस विद्यार्थी को हटाना चाहते हैं?'))) return;
    try {
      const res = await api.delete(`/api/students/${studentId}`, token);
      if (res.success) {
        setToast({ message: L('Student deleted!', 'विद्यार्थी हटा दिया गया!'), type: 'success' });
        loadData();
      } else {
        setToast({ message: res.detail || L('Error deleting', 'हटाने में त्रुटि'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: L('Connection error', 'कनेक्शन त्रुटि'), type: 'error' });
    }
  };

  // Bulk delete selected students
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      setToast({ message: L(`Deleting ${selectedIds.length} students...`, `${selectedIds.length} विद्यार्थी हटाए जा रहे हैं...`), type: 'success' });
      const res = await api.post('/api/students/bulk-delete', { student_ids: selectedIds }, token);
      if (res.success) {
        setToast({ message: L(`${res.deleted_count || selectedIds.length} students deleted successfully!`, `${res.deleted_count || selectedIds.length} विद्यार्थी सफलतापूर्वक हटाए गए!`), type: 'success' });
        setShowDeleteConfirm(false);
        loadData();
      } else {
        setToast({ message: res.detail || L('Error deleting', 'हटाने में त्रुटि'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: L('Connection error', 'कनेक्शन त्रुटि'), type: 'error' });
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = L(
      ['Adm. No.', 'Name', 'Gender', 'DOB', 'Phone', "Father's Name", "Mother's Name", 'Samagra ID', 'Aadhaar', 'Category', 'Status'],
      ['प्रवेश क्रमांक', 'नाम', 'लिंग', 'जन्म तिथि', 'फ़ोन', 'पिता का नाम', 'माता का नाम', 'समग्र ID', 'आधार', 'श्रेणी', 'स्थिति']
    );
    const rows = filteredStudents.map(s => [
      s.admission_number,
      s.name,
      s.gender,
      s.dob ? new Date(s.dob).toLocaleDateString('hi-IN') : '',
      s.primary_phone,
      s.father_name || '',
      s.mother_name || '',
      s.samagra_id || '',
      s.aadhaar_number || '',
      s.caste_category || '',
      s.status
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `students_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setToast({ message: L('Downloading CSV file', 'CSV फ़ाइल डाउनलोड हो रही है'), type: 'success' });
  };

  // Print student list
  const handlePrint = () => {
    const printContent = `
      <html>
      <head>
        <title>${L('Student List', 'विद्यार्थी सूची')}</title>
        <style>
          body { font-family: 'Noto Sans Devanagari', Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #d97706; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #f3f4f6; }
          .footer { margin-top: 20px; text-align: right; font-size: 10px; color: #666; }
        </style>
      </head>
      <body>
        <h1>${L('Shiksha Mitra - Student List', 'शिक्षा मित्र - विद्यार्थी सूची')}</h1>
        <p>${L('Total Students', 'कुल विद्यार्थी')}: ${filteredStudents.length} | ${L('Date', 'तिथि')}: ${new Date().toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN')}</p>
        <table>
          <thead>
            <tr>
              <th>${L('No.', 'क्र.')}</th>
              <th>${L('Adm. No.', 'प्रवेश क्रमांक')}</th>
              <th>${L('Name', 'नाम')}</th>
              <th>${L('Gender', 'लिंग')}</th>
              <th>${L('Phone', 'फ़ोन')}</th>
              <th>${L("Father's Name", 'पिता का नाम')}</th>
              <th>${L('Samagra ID', 'समग्र ID')}</th>
              <th>${L('Status', 'स्थिति')}</th>
            </tr>
          </thead>
          <tbody>
            ${filteredStudents.map((s, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${s.admission_number}</td>
                <td>${s.name}</td>
                <td>${s.gender === 'male' ? L('Male', 'पुरुष') : s.gender === 'female' ? L('Female', 'महिला') : L('Other', 'अन्य')}</td>
                <td>${s.primary_phone}</td>
                <td>${s.father_name || '-'}</td>
                <td>${s.samagra_id || '-'}</td>
                <td>${s.status === 'active' ? L('Active', 'सक्रिय') : L('Inactive', 'निष्क्रिय')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">${L('Generated by Shiksha Mitra', 'शिक्षा मित्र द्वारा निर्मित')}</div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Download PDF Report
  const handleDownloadPDF = async () => {
    try {
      setToast({ message: L('Preparing PDF...', 'PDF तैयार हो रही है...'), type: 'success' });
      const response = await fetch(`${API_URL}/api/reports/students/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `students_report_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        setToast({ message: L('PDF downloaded!', 'PDF डाउनलोड हो गई!'), type: 'success' });
      } else {
        setToast({ message: L('Error downloading PDF', 'PDF डाउनलोड में त्रुटि'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: L('Error downloading PDF', 'PDF डाउनलोड में त्रुटि'), type: 'error' });
    }
  };

  const filteredStudents = students.filter(s => {
    if (filter.class_id && s.current_class_id !== filter.class_id) return false;
    if (filter.search && !s.name.toLowerCase().includes(filter.search.toLowerCase()) && !s.admission_number.includes(filter.search)) return false;
    return true;
  });

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800">{L('Students', 'विद्यार्थी')} ({students.length})</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={handlePrint} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1" data-testid="print-students-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            {L('Print', 'प्रिंट')}
          </button>
          <button onClick={handleExportCSV} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center gap-1" data-testid="export-csv-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            {L('Export CSV', 'CSV निर्यात')}
          </button>
          <button onClick={handleDownloadPDF} className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-1" data-testid="download-pdf-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
            PDF
          </button>
          <button onClick={() => setShowColumnFilter(true)} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center gap-1" data-testid="column-filter-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
            {L('Columns', 'कॉलम')} ({visibleColumns.length})
          </button>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-1" data-testid="add-student-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            {L('New Student', 'नया विद्यार्थी')}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <input placeholder={L('Search by name or admission no...', 'नाम या प्रवेश क्रमांक से खोजें...')} value={filter.search} onChange={(e) => setFilter({...filter, search: e.target.value})}
          className="flex-1 px-4 py-2 border rounded-lg" data-testid="student-search-input" />
        <select value={filter.class_id} onChange={(e) => setFilter({...filter, class_id: e.target.value})} className="px-4 py-2 border rounded-lg" data-testid="class-filter-select">
          <option value="">{L('All Classes', 'सभी कक्षाएं')} ({students.length})</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-orange-800 font-medium">
            {L(`${selectedIds.length} students selected`, `${selectedIds.length} विद्यार्थी चुने गए`)}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
            >
              {L('Cancel', 'रद्द करें')}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm flex items-center gap-1"
              data-testid="bulk-delete-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {L('Delete', 'हटाएं')} ({selectedIds.length})
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-center">
                  <input 
                    type="checkbox" 
                    checked={selectAll && filteredStudents.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    data-testid="select-all-checkbox"
                  />
                </th>
                {allColumns.filter(c => visibleColumns.includes(c.key)).map(col => (
                  <th key={col.key} className="px-4 py-3 text-left text-sm font-medium text-gray-500">{col.label}</th>
                ))}
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{L('Actions', 'कार्रवाई')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredStudents.map(student => (
                <tr key={student.id} className={`hover:bg-gray-50 ${selectedIds.includes(student.id) ? 'bg-orange-50' : ''}`}>
                  <td className="px-3 py-3 text-center">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.includes(student.id)}
                      onChange={() => handleSelectStudent(student.id)}
                      className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      data-testid={`select-student-${student.id}`}
                    />
                  </td>
                  {visibleColumns.includes('admission_number') && (
                    <td className="px-4 py-3 font-medium">{student.admission_number}</td>
                  )}
                  {visibleColumns.includes('name') && (
                    <td className="px-4 py-3">
                      <button onClick={() => handleViewStudent(student)} className="text-left group" data-testid={`student-name-${student.id}`}>
                        <div className="font-medium text-gray-800 group-hover:text-orange-600 group-hover:underline">{student.name}</div>
                        {student.name_hindi && student.name_hindi !== student.name && (
                          <div className="text-xs text-gray-500">{student.name_hindi}</div>
                        )}
                      </button>
                    </td>
                  )}
                  {visibleColumns.includes('name_hindi') && (
                    <td className="px-4 py-3 text-gray-700">{student.name_hindi || '-'}</td>
                  )}
                  {visibleColumns.includes('class_name') && (
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{student.class_name || '-'}</span>
                    </td>
                  )}
                  {visibleColumns.includes('section_name') && (
                    <td className="px-4 py-3 text-gray-500">{student.section_name || 'A'}</td>
                  )}
                  {visibleColumns.includes('gender') && (
                    <td className="px-4 py-3 capitalize">{student.gender === 'male' ? L('Male', 'पुरुष') : student.gender === 'female' ? L('Female', 'महिला') : L('Other', 'अन्य')}</td>
                  )}
                  {visibleColumns.includes('dob') && (
                    <td className="px-4 py-3 text-gray-500 text-sm">{student.dob ? new Date(student.dob).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN') : '-'}</td>
                  )}
                  {visibleColumns.includes('primary_phone') && (
                    <td className="px-4 py-3 text-gray-500">{student.primary_phone || '-'}</td>
                  )}
                  {visibleColumns.includes('father_name') && (
                    <td className="px-4 py-3 text-gray-500">{student.father_name || '-'}</td>
                  )}
                  {visibleColumns.includes('mother_name') && (
                    <td className="px-4 py-3 text-gray-500">{student.mother_name || '-'}</td>
                  )}
                  {visibleColumns.includes('samagra_id') && (
                    <td className="px-4 py-3 text-gray-500 font-mono text-sm">{student.samagra_id || '-'}</td>
                  )}
                  {visibleColumns.includes('aadhaar_number') && (
                    <td className="px-4 py-3 text-gray-500 font-mono text-sm">{student.aadhaar_number || '-'}</td>
                  )}
                  {visibleColumns.includes('category') && (
                    <td className="px-4 py-3">
                      {(student.category || student.caste_category) && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          (student.category || student.caste_category) === 'sc' ? 'bg-blue-100 text-blue-700' :
                          (student.category || student.caste_category) === 'st' ? 'bg-green-100 text-green-700' :
                          (student.category || student.caste_category) === 'obc' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {(student.category || student.caste_category || '').toUpperCase()}
                        </span>
                      )}
                    </td>
                  )}
                  {visibleColumns.includes('religion') && (
                    <td className="px-4 py-3 text-gray-500 text-sm">{student.religion || '-'}</td>
                  )}
                  {visibleColumns.includes('caste') && (
                    <td className="px-4 py-3 text-gray-500 text-sm">{student.caste || '-'}</td>
                  )}
                  {visibleColumns.includes('address_district') && (
                    <td className="px-4 py-3 text-gray-500">{student.address_district || '-'}</td>
                  )}
                  {visibleColumns.includes('bank_account_number') && (
                    <td className="px-4 py-3 text-gray-500 font-mono text-sm">{student.bank_account_number || '-'}</td>
                  )}
                  {visibleColumns.includes('status') && (
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {student.status === 'active' ? L('Active', 'सक्रिय') : L('Inactive', 'निष्क्रिय')}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleViewStudent(student)} className="text-orange-600 hover:text-orange-800 text-sm font-medium" data-testid={`view-student-${student.id}`}>
                        {L('View', 'देखें')}
                      </button>
                      <button onClick={() => handleDeleteStudent(student.id)} className="text-red-500 hover:text-red-700 text-sm" data-testid={`delete-student-${student.id}`}>
                        {L('Delete', 'हटाएं')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredStudents.length === 0 && (
                <tr><td colSpan={visibleColumns.length + 2} className="px-4 py-8 text-center text-gray-500">{L('No students found', 'कोई विद्यार्थी नहीं मिला')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-red-600 mb-4">⚠️ {L('Delete students?', 'विद्यार्थी हटाएं?')}</h3>
            <p className="text-gray-600 mb-4">
              {L('Do you really want to delete', 'क्या आप वाकई')} <strong>{selectedIds.length} {L('students', 'विद्यार्थी')}</strong> {L('? This action cannot be undone.', 'को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {L('Cancel', 'रद्द करें')}
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                data-testid="confirm-bulk-delete-btn"
              >
                {L('Yes, Delete', 'हाँ, हटाएं')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Filter Modal */}
      {showColumnFilter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">📊 {L('Select Columns', 'कॉलम चुनें')}</h3>
              <button onClick={() => setShowColumnFilter(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">{L('Select the columns you want to show:', 'जो columns दिखाने हैं उन्हें select करें:')}</p>
            <div className="grid grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto">
              {allColumns.map(col => (
                <label key={col.key} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(col.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setVisibleColumns([...visibleColumns, col.key]);
                      } else {
                        setVisibleColumns(visibleColumns.filter(k => k !== col.key));
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">{col.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t flex gap-3">
              <button
                onClick={() => {
                  setVisibleColumns(allColumns.filter(c => c.default).map(c => c.key));
                }}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
              >
                {L('Reset to Default', 'Default पर Reset')}
              </button>
              <button
                onClick={() => setVisibleColumns(allColumns.map(c => c.key))}
                className="flex-1 py-2 border border-purple-300 bg-purple-50 rounded-lg text-purple-700 hover:bg-purple-100 text-sm"
              >
                {L('Select All', 'सभी Select')}
              </button>
              <button
                onClick={() => saveColumnPrefs(visibleColumns)}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
              >
                {L('Save', 'Save करें')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Student Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={L('New Student Registration', 'नया विद्यार्थी पंजीकरण')}>
        <StudentRegistrationForm 
          classes={classes} 
          onSubmit={handleAddStudent} 
          onCancel={() => setShowAddModal(false)}
          token={token}
        />
      </Modal>

      {/* View/Edit Student Modal */}
      <Modal isOpen={showViewModal} onClose={() => { setShowViewModal(false); setSelectedStudent(null); setEditMode(false); }} title={editMode ? L('Edit Student', 'विद्यार्थी संपादित करें') : L('Student Details', 'विद्यार्थी विवरण')}>
        {selectedStudent && (
          editMode ? (
            <StudentRegistrationForm 
              classes={classes} 
              onSubmit={handleUpdateStudent} 
              onCancel={() => setEditMode(false)}
              editStudent={selectedStudent}
              token={token}
            />
          ) : (
            <StudentDetailView student={selectedStudent} onEdit={() => setEditMode(true)} />
          )
        )}
      </Modal>
    </div>
  );
}

// Reusable detail row (module-scope to avoid unstable nested component)
const DetailRow = ({ label, value }) => (
  <div className="flex border-b border-gray-100 py-2">
    <span className="w-1/3 text-sm text-gray-600">{label}:</span>
    <span className="w-2/3 text-sm font-medium">{value || '-'}</span>
  </div>
);

// Student Detail View Component
function StudentDetailView({ student, onEdit }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="max-h-[70vh] overflow-y-auto">
      <div className="space-y-4">
        {/* Basic Info */}
        <div className="bg-orange-50 p-4 rounded-lg">
          <h3 className="font-semibold text-orange-800 mb-3">{L('Basic Info', 'मूल जानकारी')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <DetailRow label={L('Admission No.', 'प्रवेश क्रमांक')} value={student.admission_number} />
            <DetailRow label={L('Roll No.', 'रोल नंबर')} value={student.roll_number} />
            <DetailRow label={L('Name (English)', 'नाम (अंग्रेजी)')} value={student.name} />
            <DetailRow label={L('Name (Hindi)', 'नाम (हिंदी)')} value={student.name_hindi} />
            <DetailRow label={L('Gender', 'लिंग')} value={student.gender === 'male' ? L('Male', 'पुरुष') : student.gender === 'female' ? L('Female', 'महिला') : L('Other', 'अन्य')} />
            <DetailRow label={L('Date of Birth', 'जन्म तिथि')} value={formatDate(student.dob)} />
            <DetailRow label={L('Blood Group', 'रक्त समूह')} value={student.blood_group} />
            <DetailRow label={L('Medium', 'माध्यम')} value={student.medium} />
          </div>
        </div>

        {/* Identity */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-3">{L('Identity & Govt. Linkage', 'पहचान व सरकारी लिंकेज')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <DetailRow label={L('Samagra ID', 'समग्र ID')} value={student.samagra_id} />
            <DetailRow label={L('Family Samagra ID', 'परिवार समग्र ID')} value={student.samagra_family_id} />
            <DetailRow label={L('Aadhaar Number', 'आधार नंबर')} value={student.aadhaar_number} />
            <DetailRow label={L('Domicile Cert. No.', 'मूल निवास प्र.प. नं.')} value={student.domicile_certificate_number} />
          </div>
        </div>

        {/* Category */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-3">{L('Category Details', 'श्रेणी विवरण')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <DetailRow label={L('Religion', 'धर्म')} value={student.religion} />
            <DetailRow label={L('Caste', 'जाति')} value={student.caste} />
            <DetailRow label={L('Category', 'वर्ग')} value={student.caste_category?.toUpperCase()} />
            <DetailRow label="BPL" value={student.is_bpl ? L('Yes', 'हां') : L('No', 'नहीं')} />
            <DetailRow label="CWSN" value={student.is_cwsn ? L('Yes', 'हां') : L('No', 'नहीं')} />
            <DetailRow label="RTE" value={student.rte_status ? L('Yes', 'हां') : L('No', 'नहीं')} />
          </div>
        </div>

        {/* Parents */}
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="font-semibold text-purple-800 mb-3">{L('Guardian Details', 'अभिभावक विवरण')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <DetailRow label={L("Father's Name", 'पिता का नाम')} value={student.father_name} />
            <DetailRow label={L("Father's Phone", 'पिता का फ़ोन')} value={student.father_phone} />
            <DetailRow label={L("Mother's Name", 'माता का नाम')} value={student.mother_name} />
            <DetailRow label={L("Mother's Phone", 'माता का फ़ोन')} value={student.mother_phone} />
            <DetailRow label={L('Guardian', 'अभिभावक')} value={student.guardian_name} />
          </div>
        </div>

        {/* Contact */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h3 className="font-semibold text-yellow-800 mb-3">{L('Contact', 'संपर्क')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <DetailRow label={L('Primary Phone', 'प्राथमिक फ़ोन')} value={student.primary_phone} />
            <DetailRow label={L('WhatsApp', 'व्हाट्सएप')} value={student.whatsapp_number} />
            <DetailRow label={L('Email', 'ईमेल')} value={student.email} />
          </div>
          {student.address && (
            <div className="mt-2">
              <DetailRow label={L('Address', 'पता')} value={`${student.address.village_town || ''}, ${student.address.district || ''}, ${student.address.state || ''} - ${student.address.pincode || ''}`} />
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t">
        <button onClick={onEdit} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium">
          {L('Edit', 'संपादित करें')}
        </button>
      </div>
    </div>
  );
}

// Teachers Page

export default StudentsPage;
